import type { AppLocale } from '@/i18n/locales'
import type { BuffSpeechPreferences } from './speech-types'
import { SystemVoices, speechLanguage } from './system-voices'
import { ManboAudio, manboCacheSupported } from './manbo-audio'
import { usesManbo } from './manbo'

export interface SpeechSentence {
  text: string
  locale: AppLocale
  preferences: BuffSpeechPreferences
  preview?: boolean
}
export interface SpeechCallbacks {
  valid: () => boolean
  started: () => void
  completed: () => void
  failed: (reason: string) => void
  cancelled: () => void
  preparing?: (value: boolean) => void
}
export class BuffSpeechPlayer {
  readonly voices = new SystemVoices()
  readonly manbo = new ManboAudio()
  private disposed = false
  private audio?: HTMLAudioElement
  private unlocking?: Promise<void>
  private unlocked = false
  private cancelPlayback?: () => void
  constructor(private readonly report: (type: string, reason?: string) => void = () => {}) {}
  unlock(): void {
    try { if (typeof speechSynthesis !== 'undefined') speechSynthesis.resume?.() } catch { /* Playback reports the actual error. */ }
    if (manboCacheSupported() && !this.unlocked && !this.unlocking && (!this.audio || this.audio.paused)) {
      this.audio ??= new Audio()
      this.audio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAACAgICA'
      this.unlocking = this.audio.play().then(() => { this.unlocked = true }, () => {}).finally(() => { this.unlocking = undefined })
    }
  }
  play(sentence: SpeechSentence, callbacks: SpeechCallbacks): () => void {
    const controller = new AbortController()
    let finished = false, started = false, utterance: SpeechSynthesisUtterance | undefined
    let audioCleanup = () => {}
    let audioFailure: string | undefined
    const start = () => {
      if (started) return
      started = true; callbacks.preparing?.(false); callbacks.started()
    }
    const done = (error?: string) => {
      if (finished || controller.signal.aborted) return
      finished = true
      audioCleanup(); callbacks.preparing?.(false)
      if (error) callbacks.failed(error); else callbacks.completed()
    }
    const valid = () => {
      if (controller.signal.aborted || finished || this.disposed) return false
      if (started) return true // Finish the current sentence once playback has started.
      if (callbacks.valid()) return true
      finished = true; callbacks.cancelled(); return false
    }
    const native = () => {
      if (!valid()) return
      if (!this.voices.supported) { done(audioFailure === 'not-allowed' ? audioFailure : 'speech-unavailable'); return }
      const voice = this.voices.select(sentence.locale, sentence.preferences.voices?.[speechLanguage(sentence.locale)])
      if (!voice) { done(audioFailure === 'not-allowed' ? audioFailure : this.voices.voices.length ? 'voice-missing' : 'voices-unavailable'); return }
      try { utterance = new SpeechSynthesisUtterance(sentence.text) } catch { done('audio-error'); return }
      utterance.lang = voice.lang.replaceAll('_', '-'); utterance.voice = voice
      utterance.volume = Math.max(0, Math.min(1, sentence.preferences.volume))
      utterance.rate = Math.max(.5, Math.min(2, sentence.preferences.rate))
      utterance.onstart = () => {
        if (finished || controller.signal.aborted) return
        if (!valid()) { speechSynthesis.cancel(); return }
        this.report('started', 'system')
        start()
      }
      utterance.onend = () => done()
      utterance.onerror = event => done(event.error)
      this.report('provider', 'system')
      try { speechSynthesis.speak(utterance) } catch (error) { done((error instanceof Error || error instanceof DOMException) && error.name === 'NotAllowedError' ? 'not-allowed' : 'audio-error') }
    }
    const system = () => {
      const readable = this.voices.refresh()
      if (readable && !this.voices.voices.length) void this.voices.wait(controller.signal).then(native)
      else native()
    }
    const manbo = async () => {
      try {
        if (sentence.preview) {
          callbacks.preparing?.(true)
          await this.manbo.wait(controller.signal)
        }
        if (!valid()) return
        const url = await this.manbo.cachedUrl(sentence.text)
        // Finish the gesture unlock before replacing its source with the real MP3.
        await this.unlocking
        if (!valid()) return
        callbacks.preparing?.(false)
        if (!url) { this.report('fallback', 'manbo-not-ready'); system(); return }
        const audio = this.audio ??= new Audio()
        let fallingBack = false
        let timeout: ReturnType<typeof setTimeout> | undefined
        const clearAudio = () => {
          clearTimeout(timeout)
          audio.oncanplay = null; audio.onplaying = null; audio.onended = null; audio.onerror = null
          audio.pause(); audio.removeAttribute('src'); audio.load()
        }
        audioCleanup = clearAudio
        const fallback = (reason: string) => {
          if (finished || controller.signal.aborted || fallingBack) return
          if (reason === 'not-allowed') this.unlocked = false
          fallingBack = true; audioFailure = reason; clearAudio(); this.report('fallback', reason); system()
        }
        audio.volume = Math.max(0, Math.min(1, sentence.preferences.volume))
        audio.onplaying = () => {
          if (!valid()) { clearAudio(); return }
          clearTimeout(timeout)
          this.unlocked = true
          if (!started) { this.manbo.markUsed(sentence.text); this.report('started', 'manbo') }
          start()
          timeout = setTimeout(() => fallback('manbo-audio'), (Number.isFinite(audio.duration) ? audio.duration / audio.playbackRate * 1000 : 30_000) + 5000)
        }
        audio.onended = () => done()
        audio.onerror = () => fallback('manbo-audio')
        audio.oncanplay = () => {
          audio.oncanplay = null
          if (!valid()) { clearAudio(); return }
          audio.currentTime = 0
          audio.playbackRate = Math.max(.5, Math.min(2, sentence.preferences.rate))
          this.report('provider', 'manbo')
          void audio.play().catch(error => fallback((error instanceof Error || error instanceof DOMException) && error.name === 'NotAllowedError' ? 'not-allowed' : 'manbo-audio'))
        }
        audio.preload = 'auto'
        audio.src = url
        timeout = setTimeout(() => fallback('manbo-audio'), 15_000)
        audio.load()
      } catch { if (valid()) { this.report('fallback', 'manbo-audio'); system() } }
    }
    if (usesManbo(sentence.locale, sentence.preferences)) void manbo()
    else system()
    const cancel = () => {
      controller.abort(); finished = true
      audioCleanup(); callbacks.preparing?.(false)
      if (utterance && typeof speechSynthesis !== 'undefined') speechSynthesis.cancel()
    }
    this.cancelPlayback = cancel
    return cancel
  }
  dispose(): void { this.disposed = true; this.cancelPlayback?.(); this.voices.dispose(); this.manbo.dispose() }
}
